import Vue from 'vue'
import Vuex from 'vuex'
Vue.use(Vuex)
import jwtDecode from 'jwt-decode'
import store from '@/store/index'
import router from '@/router'

import axios from '@/common/axios'

// In the case of multiple api calls needing to be refreshed
// https://github.com/axios/axios/issues/450#issuecomment-247446276
let authTokenRequest
async function getAuthToken () {
    if (!authTokenRequest) {
        authTokenRequest = store.dispatch('user/refreshUserTokens')
        authTokenRequest.then(() => {
            authTokenRequest = null
        }).catch(() => {
            authTokenRequest = null
        })
    }
    return authTokenRequest
}

async function logoutOfProgram () {
    // All Vuex modules must logout here
    await store.dispatch('user/userLogout')

    router.replace({name: 'login'}).catch(err => {})
}

axios.interceptors.response.use(undefined, async (error) => {
    if (error.response.status === 401 && error.response.data.message === 'TOKEN_EXPIRED' && !error.config.__isRetryRequest) {
        try {
            let response = await getAuthToken()
            await store.dispatch('user/setUserAndTokens', {accessToken: response.data.accessToken, refreshToken: response.data.refreshToken})
            error.config.headers['Authorization'] = 'Bearer ' + store.getters['user/accessToken']
            error.config.__isRetryRequest = true
            return axios(error.config)
        } catch (error) {
            logoutOfProgram()
            return Promise.reject(error)
        }
    }

    // This is for a user that isn't logged in correctly
    if (error.response.status === 401 && error.response.data.message === 'AUTHENTICATION_ERROR') {
        logoutOfProgram()
        return Promise.reject(error)
    }

    // If someone gets here we don't want to log them out, because it's
    // more of a general error
    return Promise.reject(error)
})

const SET_USER = 'SET_USER'
const STORE_ACCESS_TOKEN = 'STORE_ACCESS_TOKEN'
const STORE_REFRESH_TOKEN = 'STORE_REFRESH_TOKEN'
const LOGOUT_USER = 'LOGOUT_USER'

const user = {
    namespaced: true,
    state: {
        user: null,
        accessToken: null,
        refreshToken: null
    },
    mutations: {
        SET_USER (state, data) {
            state.user = data
        },
        STORE_ACCESS_TOKEN (state, accessToken) {
            state.accessToken = accessToken
            localStorage.setItem('accessToken', accessToken)
        },
        STORE_REFRESH_TOKEN (state, refreshToken) {
            state.refreshToken = refreshToken
            localStorage.setItem('refreshToken', refreshToken)
        },
        LOGOUT_USER (state) {
            state.user = null
            state.accessToken = null
            state.refreshToken = null
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
        }
    },
    getters: {
        user (state) {
            return state.user
        },
        accessToken (state) {
            return state.accessToken
        },
        refreshToken (state) {
            return state.refreshToken
        }
    },
    actions: {
        async setUserAndTokens ({ dispatch, commit, getters, rootGetters }, data) {
            try {
                let decoded = jwtDecode(data.accessToken)
                commit(SET_USER, decoded.data)
                commit(STORE_ACCESS_TOKEN, data.accessToken)
                commit(STORE_REFRESH_TOKEN, data.refreshToken)
            } catch (error) {
                throw new Error(error)
            }
        },
        async userLogin ({ dispatch, commit, getters, rootGetters }, credentials) {
            try {
                const response = await axios.post('user/authenticate', {
                    username: credentials.username,
                    password: credentials.password
                })
                return await dispatch('setUserAndTokens', {accessToken: response.data.accessToken, refreshToken: response.data.refreshToken})
            } catch (error) {
                throw new Error(error)
            }
        },
        async refreshUserTokens ({ dispatch, commit, getters, rootGetters }) {
            try {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + rootGetters['user/accessToken']
                return await axios.post('user/refreshAccessToken', {
                    username: getters.user.username,
                    refreshToken: getters.refreshToken
                })
            } catch (error) {
                throw error
            }
        },
        async userLogout ({ dispatch, commit, getters, rootGetters }) {
            try {
                commit(LOGOUT_USER)
                router.replace({name: 'login'}).catch(err => {})
            } catch (error) {
                throw new Error(error)
            }
        },

        async getUserData ({ dispatch, commit, getters, rootGetters }, data) {
            try {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + rootGetters['user/accessToken']
                const response = await axios.get('user/' + data)
                //console.log('getUserData: ', response.data)
                return response.data
            } catch (error) {
                throw new Error(error)
            }
        },
        
        async userSignup ({ dispatch, commit, getters, rootGetters }, credentials) {
            try {
                return await axios.post('user/signup', {
                    firstname: credentials.firstName,
                    lastname: credentials.lastName,
                    username: credentials.username,
                    email: credentials.email,
                    password: credentials.password,
                    parent_id: credentials.parent_id,
                    mobile_phone: credentials.mobile_phone,
                    bank_id: credentials.bank_id,
                    bank_name: credentials.bank_name,
                    bank_account: credentials.bank_account,
                })
            } catch (error) {
                // let error_obj = {}
                // if (error.response) {
                //     // The request was made and the server responded with a status code
                //     // that falls out of the range of 2xx
                //     error_obj = error.response.data
                //     // console.log('error.response.data', error.response.data)
                //     // console.log('error.response.status',error.response.status)
                //     // console.log('error.response.headers',error.response.headers)
                // } else if (error.request) {
                //     // The request was made but no response was received
                //     // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                //     // http.ClientRequest in node.js
                //     console.log('error.request', error.request)
                //     error_obj = error.response.data
                // } else {
                //     // Something happened in setting up the request that triggered an Error
                //     console.log('Error', error.message)
                //     error_obj = error.message
                // }
                // //console.log('error.config', error.config)
                // //throw new Error(error_obj)
                throw error
            }
        },

        async userUpdate ({ dispatch, commit, getters, rootGetters }, credentials) {
            try {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + rootGetters['user/accessToken']
                return await axios.post('user/update', {
                    username: credentials.username,
                    old_password: credentials.old_password,
                    new_password: credentials.new_password,
                })
            } catch (error) {
                throw new Error(error)
            }
        },

        async userResetPassword ({ dispatch, commit, getters, rootGetters }, credentials) {
            try {
                return await axios.post('user/resetupdate', {
                    token: credentials.token,
                    new_password: credentials.new_password,
                })
            } catch (error) {
                throw error
            }
        },

        async checkMobile ({ dispatch, commit, getters, rootGetters }, data) {
            try {
                const response = await axios.get('user/checkmobile', {params: {mobile_phone: data.mobile_phone, recaptcha_response: data.recaptcha_response}})
                //console.log('checkMobile: ', response.data)

                return response.data
            } catch (error) {
                throw new Error(error)
            }
        },

        async checkForgotUser ({ dispatch, commit, getters, rootGetters }, data) {
            try {
                const response = await axios.get('user/checkforgot', {params: {mobile_phone: data.mobile_phone, username: data.username, recaptcha_response: data.recaptcha_response}})
                //console.log('checkMobile: ', response.data)

                return response.data
            } catch (error) {
                throw new Error(error)
            }
        },

        async requestPIN ({ dispatch, commit, getters, rootGetters }, data) {
            
            try {
                const response = await axios.get('user/requestverify', {params: {number: data, brand: process.env.VUE_APP_SITE}})
                //console.log('requestPIN: ', response.data)

                return response.data
            } catch (error) {
                throw error
            }
        },

        async verifyCode ({ dispatch, commit, getters, rootGetters }, data) {

            try {
                const response = await axios.get('user/verifycode', {params: {request_id: data.request_id, code: data.pin}})
                //console.log('verifyCode: ', response.data)

                return response.data
            } catch (error) {
                throw error
            }

        },

        async cancelVerify ({ dispatch, commit, getters, rootGetters }, data) {

            try {
                const response = await axios.get('user/cancelverify', {params: {request_id: data, cmd: 'cancel'}})
                //console.log('cancelVerify: ', response.data)

                return response.data
            } catch (error) {
                throw new Error(error)
            }
        },

        async userForgot ({ dispatch, commit, getters, rootGetters }, credentials) {
            try {
                return await axios.post('user/forgot', {
                    email: credentials.email,
                    url: process.env.VUE_APP_URL + '/user/reset',
                    type: 'web'
                })
            } catch (error) {
                throw new Error(error)
            }
        },
        async userReset ({ dispatch, commit, getters, rootGetters }, credentials) {
            try {
                return await axios.post('user/resetPassword', {
                    password: credentials.password,
                    passwordResetToken: credentials.passwordResetToken,
                    email: credentials.email
                })
            } catch (error) {
                throw new Error(error)
            }
        }
        
    }
}

export default user
